import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, unwrap, type Schemas } from '@/api/client';
import { multipart } from '@/api/multipart';
import { Button, FieldHint, FileTile, Modal, ModalFooter, ModalHeader, Pill, Segmented, useModalTitleId, useToast, type Tone } from '@/design-system';
import { formatNumber } from '@/lib/format';
import { errorText } from '@/shell/errorText';
import { SOURCE_NOTICE_VERSION } from './NewOrganizationModal';
import s from './organizations.module.css';

type BulkImport = Schemas['BulkImportOut'];
type Row = Schemas['RowOut'];
type RowStatus = Row['status'];

export const TEMPLATE_URL = '/templates/organizations-import.csv';
const ACTIVE = new Set(['queued', 'running']);
const TONE: Record<RowStatus, Tone> = { valid: 'ok', created: 'ok', error: 'err', duplicate: 'warn', exists: 'mute', skipped: 'warn' };

export async function uploadImport(file: File): Promise<BulkImport> {
  const { body, contentType } = await multipart({ file });
  return unwrap(
    api.POST('/admin/organization-imports', {
      body: { file: '' },
      bodySerializer: () => body as unknown as BodyInit,
      headers: { 'Content-Type': contentType },
    }),
  );
}

export const importQuery = (id: string, live: boolean) => ({
  queryKey: ['organization-imports', id],
  queryFn: () => unwrap(api.GET('/admin/organization-imports/{import_id}', { params: { path: { import_id: id } } })),
  refetchInterval: live ? 1500 : (false as const),
});

/** GET …/result.csv with the session, saved as a file. */
export async function downloadResult(id: string): Promise<void> {
  const text = await unwrap(api.GET('/admin/organization-imports/{import_id}/result.csv', { params: { path: { import_id: id } }, parseAs: 'text' }));
  const url = URL.createObjectURL(new Blob([text as unknown as string], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `organizations-import-${id}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * «Импорт из файла» (P8-A06, additive to DESIGN_SPEC §4.2): a CSV or XLSX of organizations. Upload gives
 * the dry run (nothing created yet): every row's status, its errors and what it duplicates; «Создать»
 * confirms it as a job and the modal follows it to the result CSV. Research for each new organization is
 * optional and starts a few at a time. The source notice (Q4) is shown as in «Новая организация».
 */
export function BulkImportModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation('organizations');
  const { t: tc } = useTranslation('common');
  const tv = t as unknown as (k: string, o?: Record<string, unknown>) => string;
  const titleId = useModalTitleId();
  const toast = useToast();
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [report, setReport] = useState<BulkImport | null>(null);
  const [research, setResearch] = useState<'yes' | 'no'>('yes');
  const [problem, setProblem] = useState<string | null>(null);
  const confirmed = !!report && report.status !== 'dry_run';
  const live = useQuery({ ...importQuery(report?.id ?? '', ACTIVE.has(report?.status ?? '')), enabled: confirmed });
  const current = live.data ?? report;
  const running = !!current && ACTIVE.has(current.status);

  const check = useMutation({
    mutationFn: (f: File) => uploadImport(f),
    onSuccess: (r) => {
      setProblem(null);
      setReport(r);
    },
    onError: (err) => setProblem(errorText(err, tc)),
  });
  const confirm = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/admin/organization-imports/{import_id}/confirm', {
          params: { path: { import_id: report?.id ?? '' } },
          body: { start_research: research === 'yes', notice_version: SOURCE_NOTICE_VERSION },
        }),
      ),
    onSuccess: (r) => setReport(r),
    onError: (err) => setProblem(errorText(err, tc)),
  });
  const close = () => {
    if (confirmed) {
      void qc.invalidateQueries({ queryKey: ['organizations'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
    }
    onClose();
  };

  const counts = current?.counts ?? {};
  const valid = counts.valid ?? 0;
  const detail = (r: Row): string => {
    if (r.errors.length) return r.errors.map((e) => tv(`bulk.error.${e.code}`, { defaultValue: e.code })).join(' · ');
    const d = r.duplicate_of;
    if (d) {
      if (d.by === 'file') return t('bulk.dup_file', { line: Number(d.line ?? 0) });
      return tv(`bulk.dup.${String(d.by)}`, { defaultValue: String(d.by) });
    }
    if (r.research === 'started') return t('bulk.research_started');
    if (r.research === 'pending') return t('bulk.research_pending');
    return '';
  };

  return (
    <Modal open onClose={close} labelledBy={titleId}>
      <ModalHeader id={titleId} title={t('bulk.title')} sub={t('bulk.sub')} onClose={close} closeLabel={tc('actions.close')} />
      <div className={s.form}>
        {!report && (
          <>
            <FileTile icon="file-spreadsheet" accept=".csv,.xlsx,text/csv" onFiles={(f) => setFile(f[0] ?? null)}>
              {file ? <span className={s.fileName}>{file.name}</span> : t('bulk.file')}
            </FileTile>
            <a className={s.bulkTemplate} href={TEMPLATE_URL} download>
              {t('bulk.template')}
            </a>
            <FieldHint>{t('new.source_notice')}</FieldHint>
          </>
        )}
        {current && (
          <>
            <div className={s.bulkCounts} role="status">
              {(['valid', 'created', 'error', 'duplicate', 'exists', 'skipped'] as const)
                .filter((k) => (counts[k] ?? 0) > 0 && (k !== 'valid' || !confirmed))
                .map((k) => (
                  <Pill key={k} tone={TONE[k]} size="sm">
                    {tv(`bulk.count.${k}`, { count: counts[k] ?? 0 })}
                  </Pill>
                ))}
              {running && <span className={s.bulkRunning}>{t('bulk.running')}</span>}
            </div>
            <div className={s.bulkRows} role="table" aria-label={t('bulk.rows_label')}>
              {current.rows.map((r) => (
                <div key={r.line} role="row" className={s.bulkRow}>
                  <span role="cell" className={s.bulkLine}>
                    {r.line}
                  </span>
                  <span role="cell" className={s.bulkName}>
                    {String(r.data.name ?? '') || tc('dash')}
                  </span>
                  <span role="cell">
                    <Pill tone={TONE[r.status]} size="sm">
                      {t(`bulk.status.${r.status}`)}
                    </Pill>
                  </span>
                  <span role="cell" className={s.bulkDetail}>
                    {detail(r)}
                  </span>
                </div>
              ))}
            </div>
            {!confirmed && valid > 0 && (
              <div className={s.group}>
                <span className={s.groupLabel}>{t('bulk.research')}</span>
                <Segmented
                  variant="form"
                  label={t('bulk.research')}
                  value={research}
                  onChange={setResearch}
                  className={s.alignStart}
                  options={[
                    { value: 'yes', label: t('bulk.research_yes') },
                    { value: 'no', label: t('bulk.research_no') },
                  ]}
                />
              </div>
            )}
          </>
        )}
        {problem && (
          <span className={s.problem} role="alert">
            {problem}
          </span>
        )}
        <ModalFooter padTop>
          <Button size={46} variant="secondary" onClick={close}>
            {confirmed ? tc('actions.close') : tc('actions.cancel')}
          </Button>
          {!report && (
            <Button size={46} trailingIcon="arrow-right" disabled={!file || check.isPending} onClick={() => file && check.mutate(file)}>
              {t('bulk.check')}
            </Button>
          )}
          {report && !confirmed && (
            <Button size={46} icon="check" disabled={!valid || confirm.isPending} onClick={() => confirm.mutate()}>
              {t('bulk.create', { count: valid, n: formatNumber(valid) })}
            </Button>
          )}
          {confirmed && (
            <Button
              size={46}
              icon="arrow-down"
              disabled={running}
              onClick={() => void downloadResult(current?.id ?? '').catch((err: unknown) => toast(errorText(err, tc)))}
            >
              {t('bulk.result')}
            </Button>
          )}
        </ModalFooter>
      </div>
    </Modal>
  );
}
